import URL from 'core-js-pure/features/url';
import URLSearchParams from 'core-js-pure/features/url-search-params';
import { CoralInterceptor } from './interceptor';
/**
 * Interceptor that handles HTTP requests based on the Coral operation bindings.
 *
 * @see https://w.amazon.com/bin/view/Coral/Manual/HttpBinding/
 */
export class CoralClientHttpBindingInterceptor extends CoralInterceptor {
    constructor(bindings = {}) {
        super();
        this.bindings = bindings;
    }
    intercept(request, next) {
        const operationName = request.operation;
        const coralOperationHttpBinding = this.bindings[operationName];
        // Don't bother intercepting if the operation has not been configured.
        if (!coralOperationHttpBinding)
            return next.handle(request);
        // Determine the path of the operation
        const path = this.resolvePath(coralOperationHttpBinding, request);
        // Create a new request with the updated path and method properties.
        // If the HTTP method does not support a body, remove it from the request.
        const newRequest = request.clone({
            body: this.supportsHttpBody(coralOperationHttpBinding.method) ? request.body : null,
            method: coralOperationHttpBinding.method,
            url: this.constructUrl(request.url, path),
        });
        return next.handle(newRequest);
    }
    /*
     * The modeled path can contain Labels, eg. getBeer/{beerId}/?brewery={breweryId}
     *
     * This method fetches the 'beerId' and 'breweryId' properties from the request body, substitutes the values
     * in the URL path, and returns a new path.
     *
     * In the above case, the method will resolve the beerId and breweryId properties from the request body.
     *
     * If the label cannot be resolved with a primitive value, it is replaced with an empty string, unless the label
     * makes up the entirety of a query parameter value. In that case, the query parameter is excluded from the
     * resolved path entirely.
     */
    resolvePath(coralOperationHttpBinding, request) {
        // a prefix is necessary because the URL API only parses absolute URLs
        const urlPrefix = 'http://placeholder' + (coralOperationHttpBinding.path.startsWith('/') ? '' : '/');
        // Extract everything before the query string out of the raw URL and do label find/replace before
        // parsing with the URL API to avoid the '{' and '}' characters for labels getting URL-encoded. Otherwise,
        // the label find/replace that depends on these characters would break.
        // See section 3.6 here: http://www.faqs.org/rfcs/rfc3986.html
        const [pathTemplate, ...remainingURLComponents] = coralOperationHttpBinding.path.split('?');
        const path = this.replaceLabels(pathTemplate, coralOperationHttpBinding, request);
        const url = new URL(`${urlPrefix}${[path, ...remainingURLComponents].join('?')}`);
        // for the query string, we require more complex logic to potentially omit parameters with no resolved value
        // from the URL
        url.search = this.resolveQuery(url.searchParams, coralOperationHttpBinding, request);
        return url.toString().replace(urlPrefix, '');
    }
    replaceLabels(template, coralOperationHttpBinding, request) {
        // tslint:disable-next-line:no-let The path is generated by resolving the labels.
        let value = template;
        const labels = this.findLabels(value);
        if (!labels || labels.length === 0)
            return value;
        // No label transformations are available, return what we have.
        if (!coralOperationHttpBinding.labelTransformations)
            return value;
        labels.forEach(label => {
            const labelValue = this.resolveLabel(label, coralOperationHttpBinding, request) || '';
            value = value.replace(label, `${labelValue}`);
        });
        return value;
    }
    findLabels(s) {
        // The regex match extracts the '{}' groups into a list.
        return s.match(/{([a-zA-Z]+)}/g);
    }
    resolveQuery(query, coralOperationHttpBinding, request) {
        const newQuery = new URLSearchParams();
        for (const [key, value] of query) {
            const newValue = this.replaceLabelsInQueryParam(value, coralOperationHttpBinding, request);
            if (newValue !== undefined) {
                newQuery.append(key, newValue);
            }
        }
        return newQuery.toString();
    }
    replaceLabelsInQueryParam(template, coralOperationHttpBinding, request) {
        const labels = this.findLabels(template);
        if (labels && labels.length === 1 && labels[0] === template) {
            // If there is one label in the query parameter value and the label makes up the entire query parameter,
            // simply resolve the parameter to that value. If we cannot resolve the label to a primitive value,
            // the undefined result returned from here will result in that parameter not getting included in the final path.
            return this.resolveLabel(labels[0], coralOperationHttpBinding, request);
        }
        else {
            // Otherwise do simple find/replace as we would for any other segment of the url.
            return this.replaceLabels(template, coralOperationHttpBinding, request);
        }
    }
    resolveLabel(label, coralOperationHttpBinding, request) {
        // Remove the braces
        const labelProperty = label.replace(/{|}/g, '');
        // Fetch the request property name that should be substituted for the label. The label string itself is used as a fallback.
        // tslint:disable-next-line:no-non-null-assertion Non-null check on labelTransformations is performed in resolvePath().
        const labelTransformationProperty = coralOperationHttpBinding.labelTransformations[labelProperty] || labelProperty;
        const body = request.body;
        // The body is missing the required property, nothing to substitute.
        if (!body || !body.hasOwnProperty || !body.hasOwnProperty(labelTransformationProperty))
            return;
        const labelPropertyValue = body[labelTransformationProperty];
        // For now, object values (including Arrays) are not supported for substitution.
        if (typeof labelPropertyValue === 'object')
            return;
        return labelPropertyValue;
    }
    supportsHttpBody(method) {
        return !['GET', 'HEAD'].includes(method);
    }
    constructUrl(url, path) {
        const urlPrefix = url.endsWith('/') ? url : url + '/';
        const pathSuffix = path.startsWith('/') ? path.substring(1) : path;
        return urlPrefix + pathSuffix;
    }
}
